import React from 'react';
import { useTranslation } from 'react-i18next';

import { Checkbox, Icon, Text, TextArea } from '@/nova/components';

import { IngredientSection } from '../../../art-style/components/IngredientSection';
import type { BannerKitCatalog, Palette } from '../../banner-kit-api';
import type { BrandTheme } from '../../brand-theme-api';
import type { PlayableParams, PlayableSchema, PlayableTemplate } from '../../playable-api';
import { LegalLineField } from '../BannerKitComposer/LegalLineField';
import { LumioGlyph } from '../LumioMark';
import { BrandThemeFields } from './BrandThemeFields';
import { PlayableLinksSection } from './PlayableLinksSection';
import { SchemaForm } from './SchemaForm';
import * as S from './PlayableComposer.styles';

/** The composer rail's ingredient sections (one open at a time). */
export type RailSection =
  | 'subject'
  | 'language'
  | 'template'
  | 'settings'
  | 'formats'
  | 'art-style'
  | 'character'
  | 'direction'
  | 'brand'
  | 'legal'
  | 'links';

export interface BannerRailProps {
  params: PlayableParams;
  /** The chosen style (undefined while the registry loads). */
  template?: PlayableTemplate;
  palette: Palette;
  /** The brand's website theme (null when the site has none). */
  brandTheme: BrandTheme | null;
  /** The banner already carries the theme (Reset would change nothing). */
  onBrandTheme: boolean;
  catalog: BannerKitCatalog | null;
  /** The brand's bare host, for the legal line's `{domain}`. */
  domain: string;
  /** No edits (an art run is starting or in flight). */
  locked: boolean;
  filling: boolean;
  canFill: boolean;
  onFill: () => void;
  /** Apply an edit to the CURRENT document (the composer's latest, not this render's). */
  edit: (fn: (cur: PlayableParams) => PlayableParams) => void;
  onPalette: (key: keyof Palette, value: string) => void;
  onResetTheme: () => void;
  onToggleFormat: (format: string, on: boolean) => void;
  /** The open section (null: all closed). */
  open: RailSection | null;
  onToggle: (id: RailSection) => void;
  /** The brand's art style and its character (the composer's own sections), shown before the art direction. */
  artStyleSections?: React.ReactNode;
  /** The creatives dev flag ("777devdebug"): also show the raw generation controls (Lumio's fill, the motion, the
   *  brand colours), the legal line and the click-through. */
  devDebug: boolean;
}

/** The config field the copy lock owns (the rest of the style's schema is the motion form). */
const OFFER_LINE = 'offer_line';

/** A style's schema without the offer line (the subject's own sub-line, never edited), for the motion form. Pure. */
export const motionSchema = (schema: PlayableSchema): PlayableSchema => {
  const properties = { ...(schema.properties ?? {}) };
  delete properties[OFFER_LINE];
  return { ...schema, properties, required: (schema.required ?? []).filter((k) => k !== OFFER_LINE) };
};

/** "300×250" for a `WxH` format. Pure. */
export const formatLabel = (format: string): string => format.replace('x', '×');

export { linkHost } from './PlayableLinksSection';

/**
 * The HTML5 banner composer's setup sections (under the subject, which the composer draws), as simple as the image
 * banner's: the style, the sizes, the brand's art style and character, the art direction. The copy is the subject's
 * own and not edited here. The brand facts are automatic: the colours, button and fonts come from the website theme the
 * banner was made with, the legal line is on with the brand's preset, and the click-through is the version's tracking
 * link (the casino page of the subject when it has none). Behind the creatives dev flag the raw controls show too:
 * Lumio's fill, the style's motion settings, the brand colours, the legal line and the click-through. Everything but
 * the style, the colours and the art direction is drawn live, so it never needs new art.
 */
export const BannerRail = ({
  params,
  template,
  palette,
  brandTheme,
  onBrandTheme,
  catalog,
  domain,
  locked,
  filling,
  canFill,
  onFill,
  edit,
  onPalette,
  onResetTheme,
  onToggleFormat,
  open,
  onToggle,
  artStyleSections,
  devDebug,
}: BannerRailProps): React.ReactElement => {
  const { t } = useTranslation();
  const motion = template ? motionSchema(template.schema) : null;
  return (
    <>
      <IngredientSection
        id="h5-style"
        title={t('Style')}
        icon="vibe"
        summary={template?.label ?? params.template_id}
        open={open === 'template'}
        onToggle={() => onToggle('template')}
      >
        {/* The style is chosen once, before the composer (its picker), like a mini-game's game: read-only here. */}
        <S.TemplateRow>
          <S.TemplateName raw text={template?.label ?? params.template_id} />
        </S.TemplateRow>
        <S.LockNote>
          <Icon name="lock" size={12} />
          <Text
            tag="span"
            text="The style is set when the banner is made. Make a new HTML5 banner to use another one."
          />
        </S.LockNote>
        {devDebug ? (
          <>
            <S.FillRow>
              <S.LumioBtn type="button" onClick={onFill} disabled={locked || filling || !canFill}>
                {/* Lumio glyph for this AI affordance (never an approximated sparkle/star). */}
                <LumioGlyph size={13} />
                {filling ? <Text tag="span" text="Filling…" /> : <Text tag="span" text="Fill with Lumio" />}
              </S.LumioBtn>
            </S.FillRow>
            {brandTheme ? (
              <S.Hint text="Colours, button and fonts come from your website's theme. Lumio tunes the motion for your subject." />
            ) : (
              <S.Hint text="Lumio picks the colours and tunes the motion for your subject." />
            )}
          </>
        ) : null}
      </IngredientSection>

      {template ? (
        <IngredientSection
          id="h5-sizes"
          title={t('Sizes')}
          icon="grid"
          summary={t('{{count}} sizes', { count: params.formats.length })}
          open={open === 'formats'}
          onToggle={() => onToggle('formats')}
        >
          <S.CheckRow>
            {template.formats.map((format) => (
              <Checkbox
                key={format}
                checked={params.formats.includes(format)}
                onChange={(on) => onToggleFormat(format, on)}
                label={formatLabel(format)}
                disabled={locked || (params.formats.length === 1 && params.formats.includes(format))}
              />
            ))}
          </S.CheckRow>
          <S.Hint text="Each size gets its own background, painted to leave room for the text." />
        </IngredientSection>
      ) : null}

      {devDebug && motion && Object.keys(motion.properties ?? {}).length > 0 ? (
        <IngredientSection
          id="h5-motion"
          title={t('Motion')}
          icon="settings"
          open={open === 'settings'}
          onToggle={() => onToggle('settings')}
        >
          <SchemaForm
            schema={motion}
            value={params.config}
            onChange={(config) => {
              // Only the motion fields come from this form; the offer line (and anything else) stays as it is now.
              const keys = Object.keys(motion.properties ?? {});
              const fields = Object.fromEntries(Object.entries(config).filter(([k]) => keys.includes(k)));
              edit((cur) => ({ ...cur, config: { ...cur.config, ...fields } }));
            }}
            disabled={locked}
          />
          <S.Hint text="At most three loops, and every size is still by 30 seconds." />
        </IngredientSection>
      ) : null}

      {artStyleSections}

      <IngredientSection
        id="h5-direction"
        title={t('Direction')}
        icon="document"
        optional
        summary={params.art_direction.trim() || t('Optional')}
        open={open === 'direction'}
        onToggle={() => onToggle('direction')}
      >
        <TextArea
          label={t('Style, mood or colours')}
          value={params.art_direction}
          onChange={(v) => edit((cur) => ({ ...cur, art_direction: v }))}
          placeholder={t('For example: neon night, deep purple and gold.')}
          rows={3}
          disabled={locked}
        />
      </IngredientSection>

      {devDebug ? (
        <>
          <IngredientSection
            id="h5-brand"
            title={t('Brand colours')}
            icon="palette"
            summary={onBrandTheme ? t('Your website theme') : t('Custom colours')}
            open={open === 'brand'}
            onToggle={() => onToggle('brand')}
          >
            <BrandThemeFields
              palette={palette}
              themed={brandTheme !== null}
              onTheme={onBrandTheme}
              ad="banner"
              locked={locked}
              onPalette={onPalette}
              onReset={onResetTheme}
            />
          </IngredientSection>

          <IngredientSection
            id="h5-legal"
            title={t('Legal line')}
            icon="shield"
            summary={params.legal.enabled ? t('On') : t('Off')}
            open={open === 'legal'}
            onToggle={() => onToggle('legal')}
          >
            <LegalLineField
              legal={params.legal}
              catalog={catalog}
              domain={domain}
              disabled={locked}
              hint={t('Shown on every size as live text, so changing it never repaints the art.')}
              onChange={(legal) => edit((cur) => ({ ...cur, legal }))}
            />
          </IngredientSection>

          <PlayableLinksSection
            id="h5-links"
            landingUrl={params.landing_url}
            onLandingUrl={(v) => edit((cur) => ({ ...cur, landing_url: v }))}
            locked={locked}
            open={open === 'links'}
            onToggle={() => onToggle('links')}
          />
        </>
      ) : null}
    </>
  );
};

export default BannerRail;
